import {
  isCryptoAddressValid,
  useCheckCryptoAddressMutation,
} from '@/entities/crypto-address';
import type { UseFormReturn } from 'react-hook-form';
import type { CreateCryptoAddressFormValues, NetworkOption } from './types';
import { useTranslation } from 'react-i18next';
import { useCreateCryptoAddress } from '../api';
import { useDebounceCallback, useToast } from '@/shared/lib';
import { useCallback, useEffect } from 'react';

type Props = {
  form: UseFormReturn<CreateCryptoAddressFormValues>;
  networkOptions: NetworkOption[];
  onSuccess: () => void;
};

type Params = {
  validateCryptoAddress: () => Promise<boolean>;
  validateCryptoAddressDebounced: () => void;
  createAddress: () => Promise<void>;
  isPending: boolean;
};

export const useCreateCryptoAddressFormLogic = ({
  form,
  networkOptions,
  onSuccess,
}: Props): Params => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    mutateAsync: createCryptoAddress,
    isPending: isCreatingCryptoAddressPending,
  } = useCreateCryptoAddress({
    onSuccess: () => {
      toast.success(t('savedAddresses.cryptoAddress.creation.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.cryptoAddress.creation.result.error')
      );
    },
  });

  const {
    mutateAsync: checkCryptoAddress,
    isPending: isCheckingCryptoAddressPending,
  } = useCheckCryptoAddressMutation();

  const setCryptoAddressError = useCallback(
    (errorMsg?: string) => {
      form.setError('cryptoAddress', {
        type: 'validate',
        message:
          errorMsg ??
          t('savedAddresses.cryptoAddress.validation.invalidAddress'),
      });
    },
    [form, t]
  );

  const validateCryptoAddress = useCallback(async (): Promise<boolean> => {
    const { networkName, cryptoAddress } = form.getValues();
    if (!networkName || !cryptoAddress) return false;

    const response = await checkCryptoAddress({
      network: networkName,
      address: cryptoAddress,
    });

    const isValid = isCryptoAddressValid(response.result);

    if (isValid) {
      form.clearErrors('cryptoAddress');
    } else {
      setCryptoAddressError(response.resultInvalidMessage ?? undefined);
    }

    return isValid;
  }, [form, checkCryptoAddress, isCryptoAddressValid, setCryptoAddressError]);

  const validateCryptoAddressDebounced = useDebounceCallback(() => {
    return validateCryptoAddress();
  }, 500);

  const syncNetworkFieldWithAsset = useCallback(
    (networks: NetworkOption[]) => {
      const assetCode = form.getValues('assetCode');

      form.clearErrors('networkName');

      if (!assetCode) {
        form.setValue('networkName', '');
        return;
      }

      const currentNetwork = form.getValues('networkName');

      if (currentNetwork && networks.length > 0) {
        const network = networks.find(n => n.code === currentNetwork);

        if (network) {
          form.setValue('networkName', network?.value);
        }
        return;
      }

      if (networks.length === 1) {
        form.setValue('networkName', networks[0].value);
        return;
      }

      form.setValue('networkName', '');
    },
    [form]
  );

  const createAddress = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (!(await validateCryptoAddress())) return;

    const { networkName, cryptoAddress, templateName } = form.getValues();
    if (!networkName || !cryptoAddress || !templateName) return;

    await createCryptoAddress({
      networkName,
      data: {
        templateName,
        cryptoAddress,
      },
    });
  }, [form, validateCryptoAddress, createCryptoAddress]);

  useEffect(() => {
    syncNetworkFieldWithAsset(networkOptions);
    validateCryptoAddress();
  }, [syncNetworkFieldWithAsset, validateCryptoAddress, networkOptions]);

  return {
    validateCryptoAddress,
    validateCryptoAddressDebounced,
    createAddress,
    isPending: isCreatingCryptoAddressPending || isCheckingCryptoAddressPending,
  };
};
